import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ClipboardList, Stethoscope, Users, Activity, ArrowRight, Shield, Heart,
  LogIn, Eye, EyeOff, AlertCircle, Info, UserCog, HeartPulse, QrCode, Building2,
  ShieldCheck,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';

/* ═══ 9 RUOLI raggruppati per Area ═══ */
const roleGroups = [
  {
    area: 'Rilevazione del Bisogno',
    roles: [
      {
        id: 'pua',
        title: 'Operatore PUA',
        description: 'Punto Unico di Accesso: raccolta segnalazioni dal cittadino e invio alla COT.',
        icon: ClipboardList,
        path: '/pua',
        stats: '8 segnalazioni inviate',
      },
      {
        id: 'mmg',
        title: 'MMG (Medico Medicina Generale)',
        description: 'Compilazione Modulo 431, invio richiesta ADI al PUA e monitoraggio stato pratica.',
        icon: HeartPulse,
        path: '/mmg',
        stats: '2 bozze, 3 inviate',
      },
      {
        id: 'ospedale',
        title: 'Medico Ospedaliero / COO',
        description: 'Richiesta attivazione ADI per dimissioni protette. Vista reparto e stato pratiche.',
        icon: Building2,
        path: '/ospedale',
        stats: '4 dimissioni in corso',
      },
    ],
  },
  {
    area: 'Triage',
    highlight: true,
    roles: [
      {
        id: 'segreteria',
        title: 'COT (Centrale Operativa Territoriale)',
        description: 'Gestione triage, allarmi clinici, dimissioni protette e assegnazione Equipe UVMD.',
        icon: Shield,
        path: '/segreteria',
        stats: '12 richieste in coda',
      },
    ],
  },
  {
    area: 'Clinica e Pianificazione',
    roles: [
      {
        id: 'medico',
        title: 'Medico Valutatore UVMD',
        description: 'Valutazione multidimensionale (interRAI), agenda visite e compilazione esiti.',
        icon: Stethoscope,
        path: '/medico',
        stats: '5 valutazioni assegnate',
      },
      {
        id: 'case-manager',
        title: 'Case Manager',
        description: 'Pianificazione PAI, assegnazione operatori e generazione prestazioni a calendario.',
        icon: UserCog,
        path: '/case-manager',
        stats: '3 PAI da assegnare',
      },
    ],
  },
  {
    area: 'Erogazione e Paziente',
    roles: [
      {
        id: 'operatore',
        title: 'Operatore Sanitario',
        description: 'Prestazioni giornaliere a domicilio, scansione QR paziente e refertazione.',
        icon: Users,
        path: '/operatore',
        stats: '6 visite oggi',
      },
      {
        id: 'paziente',
        title: 'Paziente (Portale Assistito)',
        description: 'Visite in arrivo, QR Code personale, documenti e feedback sul servizio.',
        icon: QrCode,
        path: '/paziente',
        stats: '3 visite programmate',
      },
    ],
  },
  {
    area: 'Direzione e Controllo',
    roles: [
      {
        id: 'asl',
        title: 'Direzione ASL',
        description: 'Cruscotto aggregato: costi, PAI attivi, performance, esiti. Sola lettura.',
        icon: Building2,
        path: '/asl',
        stats: 'Monitoraggio globale',
      },
    ],
  },
];

/* ═══ Animation variants ═══ */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] } },
};

/* ═══ Shared Header ═══ */
function PageHeader() {
  return (
    <header className="border-b border-border bg-card">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-comfort">
          <Activity className="w-5 h-5 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-foreground">ADI</h1>
          <p className="text-xs text-muted-foreground">Assistenza Domiciliare Integrata</p>
        </div>
      </div>
    </header>
  );
}

/* ═══ STEP 1: LOGIN FORM ═══ */
function LoginForm({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    setTimeout(() => {
      const ok = onLogin(username, password);
      setIsLoading(false);
      if (!ok) setError('Credenziali non valide. Riprova.');
    }, 600);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-md space-y-8"
    >
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-muted text-primary text-xs font-medium">
          <Shield className="w-3.5 h-3.5" />
          <span>Accesso Riservato</span>
        </div>
        <h2 className="text-3xl font-semibold text-foreground">Accedi alla piattaforma</h2>
        <p className="text-sm text-muted-foreground">Inserisci le credenziali per accedere al sistema ADI</p>
      </div>

      <Card className="border border-border shadow-comfort">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium text-foreground">Utente</Label>
              <Input id="username" type="text" placeholder="Nome utente" value={username}
                onChange={(e) => setUsername(e.target.value)} autoComplete="username" data-testid="login-username-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Password" value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="login-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowPassword(v => !v)} tabIndex={-1} data-testid="login-toggle-password">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2"
                  data-testid="login-error-message">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
                </motion.div>
              )}
            </AnimatePresence>
            <Button type="submit" className="w-full gap-2" disabled={isLoading || !username || !password} data-testid="login-submit-button">
              {isLoading ? <span className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full" /> : <LogIn className="w-4 h-4" />}
              {isLoading ? 'Accesso in corso...' : 'Accedi'}
            </Button>
          </form>
          <div className="mt-4 flex items-start gap-2 text-[11px] text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 border border-border/40" data-testid="login-demo-hint">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Demo credentials: <span className="font-mono font-medium text-foreground">adiadmin</span> / <span className="font-mono font-medium text-foreground">Witapp2'25!?</span></span>
          </div>
          <div className="mt-3 flex items-center gap-2 text-[11px] text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg px-3 py-2 border border-emerald-200 dark:border-emerald-800/40" data-testid="sso-banner">
            <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Accesso tramite Gestionale Integrato (SSO) attivo</span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ═══ STEP 2: ROLE SELECTION (7 ruoli raggruppati) ═══ */
function RoleSelection({ onSelectRole }) {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="w-full max-w-5xl space-y-8"
    >
      <motion.div variants={itemVariants} className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-muted text-primary text-xs font-medium mb-2">
          <Shield className="w-3.5 h-3.5" />
          <span>Piattaforma Regionale</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold text-foreground leading-tight">
          Benvenuto nella Dashboard
        </h2>
        <p className="text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
          Seleziona il tuo profilo per accedere alla piattaforma ADI.
        </p>
      </motion.div>

      {roleGroups.map((group) => (
        <motion.div key={group.area} variants={itemVariants}>
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3 pl-1">{group.area}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {group.roles.map((role) => {
              const Icon = role.icon;
              const isHighlighted = group.highlight;
              return (
                <Card
                  key={role.id}
                  className={`group cursor-pointer transition-all duration-300 ${isHighlighted ? 'border-2 border-primary/40 hover:border-primary/60 shadow-md hover:shadow-lg bg-primary-muted/10' : 'border border-border hover:border-primary/30 hover:shadow-comfort'}`}
                  onClick={() => onSelectRole(role)}
                  data-testid={`role-card-${role.id}`}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary-muted flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:shadow-comfort transition-all duration-300">
                      <Icon className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground">{role.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{role.description}</p>
                      <p className="text-[10px] text-muted-foreground mt-2">{role.stats}</p>
                    </div>
                    <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 group-hover:bg-primary-muted transition-colors duration-300 mt-0.5">
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </motion.div>
      ))}

      <motion.div variants={itemVariants} className="text-center pt-2">
        <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Heart className="w-3.5 h-3.5 text-destructive" />
          <span>Piattaforma conforme GDPR e normative sanitarie vigenti</span>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ═══ MAIN PAGE ═══ */
export default function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, login, setCurrentRole } = useApp();

  const handleRoleSelect = (role) => {
    setCurrentRole(role.id);
    navigate(role.path);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageHeader />
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        {!isAuthenticated ? (
          <LoginForm onLogin={login} />
        ) : (
          <RoleSelection onSelectRole={handleRoleSelect} />
        )}
      </main>
    </div>
  );
}
